import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import { Add, Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { toast } from "react-toastify";
import { asnService } from "../../../utils/services/asnService";
import { trackingService } from "../../../utils/services/trackingService";
import { eventService } from "../../../utils/services/eventService";
import { supplierService } from "../../../utils/services/supplierService";
import { purchaseOrderService } from "../../../utils/services/purchaseOrderService";
import { productService } from "../../../utils/services/productService";
import { wmsService } from "../../../utils/services/wmsProvider";
import { clientConfigService } from "../../../utils/services/clientConfigService";
import { padNumberWithZeros } from "../../../utils/utility";
import AutocompleteWithPagination from "../../../components/autocomplete-with-pagination";
import MultiKeyAutocompleteWithPagination from "../../../components/multi-key-autocomplete-with-pagination";

// ── Valid drag transitions ────────────────────────────────────────────────────
export const VALID_TRANSITIONS = {
  backlog: "po_issued",
  po_issued: "supplier_confirmed",
  supplier_confirmed: "inventory_ready",
  inventory_ready: "in_transit",
  in_transit: "receiving",
  fulfillment: "pending_delivery",
};

// Stages that cannot be dragged from
export const DISABLED_DRAG_FROM = ["receiving", "in_stock", "fulfillment"];

// ── Modal title mapping ──────────────────────────────────────────────────────
const TRANSITION_TITLES = {
  backlog: "Create Purchase Order",
  po_issued: "Update ASN (Move to Supplier Confirmed)",
  supplier_confirmed: "Set Shipment Date (Move to Inventory Ready)",
  inventory_ready: "Vehicle Information (Move to In Transit)",
  in_transit: "Record Receiving Event (Move to Receiving)",
  fulfillment: "Record Delivery Event (Move to Pending Delivery)",
};

// ═══════════════════════════════════════════════════════════════════════════════
// 1. Create PO Form  (backlog → po_issued)
// ═══════════════════════════════════════════════════════════════════════════════
const CreatePOModalForm = ({ card, onSuccess, onClose }) => {
  // Derive client from card — no need to ask the user
  const clientId = card?.client?._id || card?.client;

  const [formData, setFormData] = useState({
    asn_no: "",
    supplier: null,
    sku_list: [],
    client: null,
    status: "Created",
    expected_dispatch_date: null,
    purchase_order: null,
  });
  const selectedClient = clientId ? { _id: clientId } : null;
  const [showCalendar, setShowCalendar] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const calendarRef = useRef(null);
  let orderPrefix = useRef("");

  // fetch PO prefix and count on mount
  useEffect(() => {
    if (clientId) {
      fetchOrderPrefix(clientId);
      buildInitData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildInitData = async () => {
    let cur, supplier;
    if (card?.inventoryConfig) {
      cur = new Date();
      cur.setDate(
        cur.getDate() +
          (card?.inventoryConfig?.avg_fullfillment_days_supplier || 7) +
          2,
      );

      supplier = await supplierService.get(card.inventoryConfig.supplier);
    }
    setFormData({
      asn_no: "",
      supplier: supplier || null,
      sku_list: [
        {
          id: 1,
          sku: card?.sku || null,
          quantity_requested: card?.qty || "",
          product: card.product || null,
          variant_id: card.variant_id || null,
          label: card?.variant_title || "",
          name: card?.name || "",
          inventoryConfig: card.inventoryConfig,
          warehouse: card?.warehouse || null,
        },
      ],
      client: null,
      status: "Created",
      expected_dispatch_date: cur,
      purchase_order: null,
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        e.target.getAttribute("name") !== "date-field"
      ) {
        setShowCalendar(false);
      }
    };
    if (showCalendar)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  const fetchOrderPrefix = async (clientId) => {
    try {
      const resp = await clientConfigService.fetch(
        clientId,
        1,
        0,
        "purchase_order_prefix",
      );
      if (resp?.total > 0) orderPrefix.current = resp.data[0].value || "";
      const countResp = await asnService.fetch("$limit=0", clientId);
      setFormData((prev) => ({
        ...prev,
        asn_no: `${orderPrefix.current}${padNumberWithZeros(
          (countResp?.total || 0) + 1,
        )}`,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.asn_no) errors.asn_no = "P.O. Number is required";
    if (!formData.supplier) errors.supplier = "Select a Supplier";
    if (!formData.expected_dispatch_date)
      errors.expected_dispatch_date = "Expected Dispatch Date is required";
    formData.sku_list.forEach((sku, idx) => {
      if (!sku.sku) errors[`sku_${idx}`] = "SKU is required";
      if (!sku.warehouse) errors[`warehouse_${idx}`] = "Warehouse is required";
      if (
        sku.inventoryConfig &&
        (!sku.quantity_requested ||
          sku.quantity_requested < sku.inventoryConfig.min_order_quantity)
      )
        errors[`quantity_requested_${idx}`] =
          `Quantity must be greater than or equal to ${sku.inventoryConfig.min_order_quantity}`;
      else if (!sku.quantity_requested || sku.quantity_requested < 1)
        errors[`quantity_requested_${idx}`] = "Quantity must be greater than 0";
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addSkuRow = () =>
    setFormData((prev) => ({
      ...prev,
      sku_list: [
        ...prev.sku_list,
        {
          id: prev.sku_list.length + 1,
          sku: null,
          quantity_requested: "",
          product: null,
          variant_id: null,
          label: "",
          name: "",
          inventoryConfig: null,
          warehouse: null,
        },
      ],
    }));

  const removeSkuRow = (id) =>
    setFormData((prev) => ({
      ...prev,
      sku_list: prev.sku_list.filter((r) => r.id !== id),
    }));

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      const e = { ...prev };
      delete e[field];
      return e;
    });
  };

  const handleSkuChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sku_list: prev.sku_list.map((s) =>
        s.id === id ? { ...s, [field]: value } : s,
      ),
    }));
    setFormErrors((prev) => {
      const e = { ...prev };
      delete e[`${field}_${id - 1}`];
      return e;
    });
  };

  const handleSkuSelect = (id, product) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        sku_list: prev.sku_list.map((row) =>
          row.id === id
            ? {
                id,
                label: product?.label,
                sku: product?.variants?.sku,
                product: product?._id,
                variant_id: product?.variants?._id,
                name: product.name,
                inventoryConfig: product.inventoryConfig,
                quantity_requested: row.quantity_requested,
                warehouse: row.warehouse,
                variant: product.variant,
              }
            : row,
        ),
      };
      if (product.supplier && !updated.supplier)
        updated.supplier = product.supplier;
      setFormErrors((prev) => {
        const e = { ...prev };
        delete e[`sku_${id - 1}`];
        delete e.supplier;
        return e;
      });
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const obj = {
        ...formData,
        client: clientId,
        supplier: formData.supplier?._id,
        purchase_order: formData.purchase_order?._id,
        sku_list: formData.sku_list.map((row) => ({
          sku: row.sku,
          product: row.product,
          variant_id: row.variant_id,
          quantity_requested: row.quantity_requested,
          inventory_config: row.inventoryConfig
            ? row.inventoryConfig._id
            : null,
          warehouse: row.warehouse ? row.warehouse._id : null,
        })),
      };
      const resp = await asnService.create(obj);
      if (resp) {
        toast.success("Purchase Order created successfully!", {
          autoClose: 2000,
        });
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to create Purchase Order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveClientId = clientId;

  return (
    <Box sx={{ p: 1 }}>
      <Typography fontFamily="poppins" fontSize="13px" fontWeight={500} mb={1}>
        Basic Information
      </Typography>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            sx={{ backgroundColor: "var(--bg-paper)" }}
            size="small"
            label="P.O. Number"
            value={formData.asn_no}
            onChange={(e) => handleInputChange("asn_no", e.target.value)}
            fullWidth
            error={!!formErrors.asn_no}
            helperText={formErrors.asn_no}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutocompleteWithPagination
            disabled={
              !effectiveClientId ||
              !!(formData.purchase_order && formData.supplier)
            }
            selectedClient={selectedClient}
            backgroundColor="var(--bg-paper)"
            customLabel="Supplier"
            selectedItem={formData.supplier}
            onDataChange={(e) => {
              if (e) handleInputChange("supplier", e);
              else handleInputChange("supplier", null);
            }}
            width="100%"
            optionKey="name"
            apiEndPoint={(limit, skip, search, client) =>
              supplierService.fetch(
                search,
                client,
                `$limit=${limit}&$skip=${skip}`,
              )
            }
            errorMessage={formErrors.supplier}
            inputSx={{ mt: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutocompleteWithPagination
            key={formData.purchase_order ? "selected" : "none"}
            selectedClient={selectedClient}
            backgroundColor="var(--bg-paper)"
            customLabel="Master P.O."
            disabled={!effectiveClientId}
            selectedItem={formData.purchase_order}
            onDataChange={(e) => {
              if (e) {
                handleInputChange("purchase_order", e);
                if (e.supplier && !formData.supplier)
                  handleInputChange("supplier", e.supplier);
              } else {
                handleInputChange("purchase_order", null);
              }
            }}
            width="100%"
            optionKey="no"
            apiEndPoint={(limit, skip, search, client) =>
              purchaseOrderService.fetch(
                `$limit=${limit}&$skip=${skip}&$populate=supplier&expiration_date[$gte]=${new Date().toISOString()}&date[$lte]=${new Date().toISOString()}`,
                client,
                search,
              )
            }
            inputSx={{ mt: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="date-field"
            size="small"
            label="Expected Dispatch Date"
            value={
              formData.expected_dispatch_date
                ? dayjs(formData.expected_dispatch_date).format("YYYY-MM-DD")
                : ""
            }
            onFocus={() => setShowCalendar(true)}
            inputProps={{ readOnly: true }}
            fullWidth
            InputProps={
              formData.expected_dispatch_date
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => setShowCalendar(true)}
                          size="small"
                        >
                          <CalendarMonthOutlined fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
            error={!!formErrors.expected_dispatch_date}
            helperText={formErrors.expected_dispatch_date}
            sx={{ backgroundColor: "var(--bg-paper)" }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />
      <Typography fontFamily="poppins" fontSize="13px" fontWeight={500} mb={1}>
        SKU List
      </Typography>
      <Box
        sx={{
          maxHeight: 220,
          overflowY: "auto",
          border: "1px solid var(--border-color)",
          borderRadius: 1,
          p: 1,
        }}
      >
        {formData.sku_list.map((row, idx) => (
          <Grid container spacing={1} alignItems="center" key={row.id} mb={1}>
            {console.log(row)}
            <Grid item xs={12} sm={5}>
              <MultiKeyAutocompleteWithPagination
                disabled={!effectiveClientId}
                supplier={formData.supplier?._id}
                selectedClient={selectedClient}
                backgroundColor="var(--bg-paper)"
                customLabel="SKU"
                selectedItem={row || null}
                onDataChange={(sku) => {
                  if (sku) handleSkuSelect(row.id, sku);
                  else handleSkuChange(row.id, "sku", null);
                }}
                width="100%"
                primaryKey={"sku"}
                parenthesisKey={"label"}
                secondLineKey={"name"}
                apiEndPoint={(limit, skip, search, client, supplier) =>
                  productService.fetchSKU(
                    client,
                    search,
                    limit,
                    skip,
                    formData.sku_list.map((x) => x.variant_id).filter(Boolean),
                    supplier,
                  )
                }
                errorMessage={formErrors[`sku_${idx}`]}
              />
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <AutocompleteWithPagination
                disabled={!effectiveClientId || row.warehouse}
                selectedClient={selectedClient}
                backgroundColor="var(--bg-paper)"
                customLabel="Warehouse"
                selectedItem={row.warehouse || null}
                onDataChange={(wh) => {
                  if (wh) handleSkuChange(row.id, "warehouse", wh);
                  else handleSkuChange(row.id, "warehouse", null);
                }}
                width="100%"
                optionKey={(item) => `${item.name} (${item.warehouse_code})`}
                apiEndPoint={(limit, skip, search, client) =>
                  wmsService.fetch(client, limit, skip, null)
                }
                errorMessage={formErrors[`warehouse_${idx}`]}
              />
            </Grid>
            <Grid item xs={10} sm={2.25}>
              <TextField
                disabled={!effectiveClientId}
                sx={{ backgroundColor: "var(--bg-paper)" }}
                label="Quantity"
                fullWidth
                type="number"
                size="small"
                value={row.quantity_requested}
                onChange={(e) =>
                  handleSkuChange(row.id, "quantity_requested", e.target.value)
                }
                inputProps={{
                  min: row?.inventoryConfig?.min_order_quantity || 1,
                }}
                error={!!formErrors[`quantity_requested_${idx}`]}
                helperText={formErrors[`quantity_requested_${idx}`]}
              />
            </Grid>
            <Grid item xs={2} sm={1} sx={{ display: "flex" }}>
              {formData.sku_list.length > 1 && (
                <IconButton
                  disabled={!effectiveClientId}
                  onClick={() => removeSkuRow(row.id)}
                  size="small"
                >
                  <Delete color="error" fontSize="small" />
                </IconButton>
              )}
              {idx === formData.sku_list.length - 1 && (
                <IconButton
                  disabled={!effectiveClientId}
                  onClick={addSkuRow}
                  size="small"
                >
                  <Add color="primary" fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
      </Box>

      {/* Calendar popup */}
      {showCalendar && (
        <Box
          ref={calendarRef}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            bgcolor: "var(--bg-paper)",
            boxShadow: 6,
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Calendar
            value={
              formData.expected_dispatch_date
                ? new Date(formData.expected_dispatch_date)
                : new Date()
            }
            onChange={(val) => {
              handleInputChange("expected_dispatch_date", val.toISOString());
              setShowCalendar(false);
            }}
            minDate={new Date()}
            className="custom-calendar"
          />
        </Box>
      )}

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Creating..." : "Create P.O."}
        </Button>
      </DialogActions>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2. Update ASN Form  (po_issued → supplier_confirmed)
//    Shows SKU information only; sets status:"Supplier Confirmed" per SKU item
// ═══════════════════════════════════════════════════════════════════════════════
const OVERPACK_TYPES = ["Corrugated Carton", "Gaylord Box", "Pallet", "Crate"];

const DispatchASNModalForm = ({ card, onSuccess, onClose }) => {
  const [asnSummary, setAsnSummary] = useState({ asn_no: "", supplier: null });
  const [skuList, setSkuList] = useState([]);
  const [skuErrors, setSkuErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Fetch ASN by _id on mount ────────────────────────────────────
  useEffect(() => {
    const fetchASN = async () => {
      try {
        const data = await asnService.fetchById(card._id);
        setAsnSummary({
          asn_no: data.asn_no || "",
          supplier: data.supplier || null,
        });
        setSkuList(
          (data.sku_list || []).map((row) => ({
            _id: row._id,
            sku: row.sku,
            quantity_requested: row.quantity_requested || "",
            quantity_accepted:
              row.quantity_accepted ?? row.quantity_requested ?? "",
            overpack_type: row.overpack_type || "",
            items_per_overpack: row.items_per_overpack || "",
            no_of_overpacks: row.no_of_overpacks || "",
            instructions: row.instructions || "",
            warehouse: row.warehouse || null,
            variant:
              row.product?.variants?.find((v) => v._id === row.variant_id) ||
              null,
            product_details: row.product || null,
          })),
        );
      } catch (e) {
        console.error(e);
        toast.error("Failed to load ASN data.");
      } finally {
        setLoading(false);
      }
    };
    fetchASN();
  }, [card._id]);

  const handleSkuChange = (id, field, value) => {
    setSkuList((prev) =>
      prev.map((s) => (s._id === id ? { ...s, [field]: value } : s)),
    );
    setSkuErrors((prev) => {
      const e = { ...prev };
      delete e[`sku_${id}_${field}`];
      return e;
    });
  };

  const validate = () => {
    const sErrors = {};
    skuList.forEach((item) => {
      const key = `sku_${item._id}`;
      if (!item.quantity_accepted || Number(item.quantity_accepted) <= 0)
        sErrors[`${key}_quantity_accepted`] = "Confirmed qty must be > 0";
      if (Number(item.quantity_accepted) > Number(item.quantity_requested))
        sErrors[`${key}_quantity_accepted`] = "Cannot exceed requested qty";
      if (!item.overpack_type)
        sErrors[`${key}_overpack_type`] = "Overpack type is required";
      if (!item.no_of_overpacks || Number(item.no_of_overpacks) <= 0)
        sErrors[`${key}_no_of_overpacks`] = "No. of overpacks must be > 0";
    });
    setSkuErrors(sErrors);
    return Object.keys(sErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        status: "Supplier Confirmed",
        sku_list: skuList.map((x) => ({
          _id: x._id,
          sku: x.sku,
          status: "Supplier Confirmed",
          quantity_requested: x.quantity_requested,
          quantity_accepted: x.quantity_accepted,
          overpack_type: x.overpack_type,
          items_per_overpack: x.items_per_overpack,
          no_of_overpacks: x.no_of_overpacks,
          instructions: x.instructions,
          warehouse: x.warehouse?._id || x.warehouse,
          product: x.product_details._id || x.product_details,
          variant_id: x.variant?._id || x.variant_id,
        })),
      };
      await asnService.edit(card._id, payload);
      toast.success("ASN updated! Card moved to Supplier Confirmed.", {
        autoClose: 2000,
      });
      onSuccess();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update ASN. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );

  const labelSx = {
    fontSize: 12,
    fontFamily: "Poppins",
    mb: 0.5,
    textAlign: "left",
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Summary banner */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          background: "rgba(99,102,241,0.06)",
          borderRadius: 1,
          border: "1px solid rgba(99,102,241,0.15)",
        }}
      >
        <Typography fontFamily="Poppins" fontSize={12} color="text.secondary">
          <strong>P.O.:</strong> {asnSummary.asn_no} &nbsp;|&nbsp;
          <strong>Supplier:</strong> {asnSummary.supplier?.name || "—"}
        </Typography>
      </Box>

      {/* ── SKU Information ── */}
      <Typography
        fontFamily="Poppins"
        fontSize={13}
        fontWeight={600}
        mb={1}
        textAlign="left"
      >
        SKU Information
      </Typography>
      <Box
        sx={{
          maxHeight: 440,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {skuList.map((row) => (
          <Box
            key={row._id}
            sx={{
              background: "var(--bg-primary)",
              border: "1px solid var(--border-color)",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Grid container spacing={2}>
              {/* SKU — read-only */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={labelSx}>
                  SKU
                </Typography>
                <TextField
                  disabled
                  size="small"
                  fullWidth
                  value={`${row?.variant?.sku || row.sku || ""}${
                    row?.variant?.title ? " (" + row.variant.title + ")" : ""
                  }${row?.product_details?.name ? " | " + row.product_details.name : ""}`}
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* Warehouse — read-only */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  Warehouse
                </Typography>
                <TextField
                  disabled
                  size="small"
                  fullWidth
                  value={
                    row.warehouse
                      ? `${row.warehouse.name || ""} (${row.warehouse.warehouse_code || ""})`
                      : "—"
                  }
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* Qty Requested — read-only */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  Qty Requested
                </Typography>
                <TextField
                  disabled
                  size="small"
                  fullWidth
                  value={row.quantity_requested}
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* Qty Confirmed */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  Qty Confirmed *
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  value={row.quantity_accepted}
                  onChange={(e) =>
                    handleSkuChange(
                      row._id,
                      "quantity_accepted",
                      e.target.value,
                    )
                  }
                  error={!!skuErrors[`sku_${row._id}_quantity_accepted`]}
                  helperText={skuErrors[`sku_${row._id}_quantity_accepted`]}
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* No. of Overpacks */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  No. of Overpacks *
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  value={row.no_of_overpacks}
                  onChange={(e) =>
                    handleSkuChange(row._id, "no_of_overpacks", e.target.value)
                  }
                  error={!!skuErrors[`sku_${row._id}_no_of_overpacks`]}
                  helperText={skuErrors[`sku_${row._id}_no_of_overpacks`]}
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* Overpack Type */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  Overpack Type *
                </Typography>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={row.overpack_type}
                  onChange={(e) =>
                    handleSkuChange(row._id, "overpack_type", e.target.value)
                  }
                  error={!!skuErrors[`sku_${row._id}_overpack_type`]}
                  sx={{
                    backgroundColor: "var(--bg-paper)",
                    "& .MuiSelect-select": { textAlign: "left" },
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select type</em>
                  </MenuItem>
                  {OVERPACK_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
                {skuErrors[`sku_${row._id}_overpack_type`] && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {skuErrors[`sku_${row._id}_overpack_type`]}
                  </Typography>
                )}
              </Grid>

              {/* Max Items / Overpack */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={labelSx}>
                  Max Items / Overpack
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  value={row.items_per_overpack}
                  onChange={(e) =>
                    handleSkuChange(
                      row._id,
                      "items_per_overpack",
                      e.target.value,
                    )
                  }
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>

              {/* Instructions */}
              <Grid item xs={12}>
                <Typography variant="body2" sx={labelSx}>
                  Instructions
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  value={row.instructions}
                  onChange={(e) =>
                    handleSkuChange(row._id, "instructions", e.target.value)
                  }
                  sx={{ backgroundColor: "var(--bg-paper)" }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Saving..." : "Confirm Supplier"}
        </Button>
      </DialogActions>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 3. Shipment Date Form  (supplier_confirmed → inventory_ready)
//    Collects confirmed_dispatch_date (optional) and shipment_date (required)
// ═══════════════════════════════════════════════════════════════════════════════
const ShipmentDateModalForm = ({ card, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    confirmed_dispatch_date: null,
    shipment_date: null,
    bol_number: "",
  });
  const [asnSummary, setAsnSummary] = useState({ asn_no: "", supplier: null });
  const [skuList, setSkuList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfirmedCal, setShowConfirmedCal] = useState(false);
  const [showShipmentCal, setShowShipmentCal] = useState(false);

  useEffect(() => {
    const fetchASN = async () => {
      try {
        const data = await asnService.fetchById(card._id);
        setAsnSummary({
          asn_no: data.asn_no || "",
          supplier: data.supplier || null,
        });
        setSkuList(data.sku_list || []);
        setFormData({
          confirmed_dispatch_date: data.confirmed_dispatch_date || null,
          shipment_date: data.shipment_date || null,
          bol_number: data.bol_no || "",
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load ASN data.");
      } finally {
        setLoading(false);
      }
    };
    fetchASN();
  }, [card._id]);

  const handleSubmit = async () => {
    if (!formData.shipment_date) {
      toast.error("Shipment date is required.");
      return;
    }
    setSubmitting(true);
    try {
      await asnService.edit(card._id, {
        confirmed_dispatch_date: formData.confirmed_dispatch_date,
        shipment_date: formData.shipment_date,
        bol_no: formData.bol_number || undefined,
        sku_list: skuList,
        status: "Inventory Ready",
      });
      toast.success("Moved to Inventory Ready!", { autoClose: 2000 });
      onSuccess();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );

  const labelSx = {
    fontSize: 12,
    fontFamily: "Poppins",
    mb: 0.5,
    textAlign: "left",
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Summary banner */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          background: "rgba(6,182,212,0.06)",
          borderRadius: 1,
          border: "1px solid rgba(6,182,212,0.2)",
        }}
      >
        <Typography fontFamily="Poppins" fontSize={12} color="text.secondary">
          <strong>P.O.:</strong> {asnSummary.asn_no} &nbsp;|&nbsp;
          <strong>Supplier:</strong> {asnSummary.supplier?.name || "—"}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={labelSx}>
            Confirmed Dispatch Date
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={
              formData.confirmed_dispatch_date
                ? dayjs(formData.confirmed_dispatch_date).format("YYYY-MM-DD")
                : ""
            }
            onClick={() => setShowConfirmedCal(true)}
            inputProps={{ readOnly: true }}
            placeholder="Select date"
            sx={{ backgroundColor: "var(--bg-paper)" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => setShowConfirmedCal(true)}
                    size="small"
                  >
                    <CalendarMonthOutlined fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={labelSx}>
            Shipment Date <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={
              formData.shipment_date
                ? dayjs(formData.shipment_date).format("YYYY-MM-DD")
                : ""
            }
            onClick={() => setShowShipmentCal(true)}
            inputProps={{ readOnly: true }}
            placeholder="Select date"
            sx={{ backgroundColor: "var(--bg-paper)" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => setShowShipmentCal(true)}
                    size="small"
                  >
                    <CalendarMonthOutlined fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Typography variant="body2" sx={labelSx}>
            BOL Number
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.bol_number}
            onChange={(e) =>
              setFormData((p) => ({ ...p, bol_number: e.target.value }))
            }
            placeholder="Enter Bill of Lading number"
            sx={{ backgroundColor: "var(--bg-paper)" }}
          />
        </Grid>
      </Grid>

      {/* Confirmed Dispatch calendar popup */}
      {showConfirmedCal && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            bgcolor: "var(--bg-paper)",
            boxShadow: 6,
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Calendar
            value={
              formData.confirmed_dispatch_date
                ? new Date(formData.confirmed_dispatch_date)
                : new Date()
            }
            onClickDay={(date) => {
              setFormData((p) => ({
                ...p,
                confirmed_dispatch_date: date.toISOString(),
              }));
              setShowConfirmedCal(false);
            }}
            minDate={new Date()}
            className="custom-calendar"
          />
        </Box>
      )}

      {/* Shipment calendar popup */}
      {showShipmentCal && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            bgcolor: "var(--bg-paper)",
            boxShadow: 6,
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Calendar
            value={
              formData.shipment_date
                ? new Date(formData.shipment_date)
                : new Date()
            }
            onClickDay={(date) => {
              setFormData((p) => ({ ...p, shipment_date: date.toISOString() }));
              setShowShipmentCal(false);
            }}
            minDate={new Date()}
            className="custom-calendar"
          />
        </Box>
      )}

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Saving..." : "Mark Inventory Ready"}
        </Button>
      </DialogActions>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 4. Vehicle Info Form  (inventory_ready → in_transit)
//    Collects driver_name and vehicle_license; sets sku_list to "Pickup Scheduled"
// ═══════════════════════════════════════════════════════════════════════════════
const VehicleInfoModalForm = ({ card, onSuccess, onClose }) => {
  const [asnSummary, setAsnSummary] = useState({ asn_no: "", supplier: null });
  const [skuList, setSkuList] = useState([]);
  const [formData, setFormData] = useState({
    driver_name: "",
    vehicle_license: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchASN = async () => {
      try {
        const data = await asnService.fetchById(card._id);
        setAsnSummary({
          asn_no: data.asn_no || "",
          supplier: data.supplier || null,
        });
        setSkuList(data.sku_list || []);
        setFormData({
          driver_name: data.additional_info?.driver_name || "",
          vehicle_license: data.additional_info?.vehicle_license || "",
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load ASN data.");
      } finally {
        setLoading(false);
      }
    };
    fetchASN();
  }, [card._id]);

  const validate = () => {
    const e = {};
    if (!formData.driver_name.trim()) e.driver_name = "Driver name is required";
    if (!formData.vehicle_license.trim())
      e.vehicle_license = "Vehicle license is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await asnService.edit(card._id, {
        additional_info: {
          driver_name: formData.driver_name,
          vehicle_license: formData.vehicle_license,
        },
        sku_list: skuList.map((x) => ({
          _id: x._id,
          sku: x.sku,
          status: "Pickup Scheduled",
          quantity_requested: x.quantity_requested,
          quantity_accepted: x.quantity_accepted,
          overpack_type: x.overpack_type,
          items_per_overpack: x.items_per_overpack,
          no_of_overpacks: x.no_of_overpacks,
          instructions: x.instructions,
          warehouse: x.warehouse?._id || x.warehouse,
          product: x.product?._id || x.product,
          variant_id: x.variant_id,
        })),
      });
      toast.success("Dispatched! Card moved to In Transit.", {
        autoClose: 2000,
      });
      onSuccess();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );

  const labelSx = {
    fontSize: 12,
    fontFamily: "Poppins",
    mb: 0.5,
    textAlign: "left",
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Summary banner */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          background: "rgba(59,130,246,0.06)",
          borderRadius: 1,
          border: "1px solid rgba(59,130,246,0.15)",
        }}
      >
        <Typography fontFamily="Poppins" fontSize={12} color="text.secondary">
          <strong>P.O.:</strong> {asnSummary.asn_no} &nbsp;|&nbsp;
          <strong>Supplier:</strong> {asnSummary.supplier?.name || "—"}{" "}
          &nbsp;|&nbsp;
          <strong>SKUs:</strong> {skuList.length}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={labelSx}>
            Driver Name <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.driver_name}
            onChange={(e) => {
              setFormData((p) => ({ ...p, driver_name: e.target.value }));
              setErrors((p) => {
                const n = { ...p };
                delete n.driver_name;
                return n;
              });
            }}
            error={!!errors.driver_name}
            helperText={errors.driver_name}
            sx={{ backgroundColor: "var(--bg-paper)" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={labelSx}>
            Vehicle License Plate <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.vehicle_license}
            onChange={(e) => {
              setFormData((p) => ({ ...p, vehicle_license: e.target.value }));
              setErrors((p) => {
                const n = { ...p };
                delete n.vehicle_license;
                return n;
              });
            }}
            error={!!errors.vehicle_license}
            helperText={errors.vehicle_license}
            sx={{ backgroundColor: "var(--bg-paper)" }}
          />
        </Grid>
      </Grid>

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Saving..." : "Dispatch to In Transit"}
        </Button>
      </DialogActions>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 5. Manual Tracking Form  (in_transit → receiving  /  fulfillment → delivered)
// ═══════════════════════════════════════════════════════════════════════════════
const ManualTrackingModalForm = ({ card, fromStage, onSuccess, onClose }) => {
  // Derive client from card — no need to ask the user
  const clientId = card?.client?._id || card?.client;
  const logisticsLegRef = useRef("");
  const [submitting, setSubmitting] = useState(false);

  const initFormData = () => ({
    assetType:
      fromStage === "in_transit"
        ? "Purchase Order"
        : fromStage === "fulfillment"
          ? "Parcel"
          : "",
    identifier: card?.uuid || card?._id || "",
    eventCode: "",
    eventDescription: "",
    eventDateTime: null,
    eventLocation: "",
    comment: "",
    logistics_leg:
      fromStage === "fulfillment"
        ? "Final Mile Delivery"
        : "Inbound Inventory Movement",
  });

  useEffect(() => {
    if (fromStage === "in_transit")
      logisticsLegRef.current = "Inbound Inventory Movement";
    else if (fromStage === "fulfillment")
      logisticsLegRef.current = "Final Mile Delivery";
  }, [fromStage]);

  const [formData, setFormData] = useState(initFormData);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const updateEventCodeAndDescription = async (e) => {
    setFormData((prev) => ({
      ...prev,
      eventDescription: e?.description || "",
      eventCode: e?.code || "",
    }));
  };

  const isFormValid = () =>
    formData.assetType &&
    formData.identifier &&
    formData.logistics_leg &&
    formData.eventCode &&
    formData.eventDateTime &&
    formData.eventLocation &&
    clientId;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const obj = {
        asset: formData.assetType,
        id: formData.identifier,
        data: {
          event_code: formData.eventCode,
          description: formData.eventDescription,
          event_date_time: formData.eventDateTime,
          location: formData.eventLocation,
          comment: formData.comment,
        },
        client: clientId || null,
      };
      await trackingService.publish(obj);
      toast.success(
        fromStage === "fulfillment"
          ? "Shipment delivered successfully!"
          : "Tracking event published successfully!",
        {
          autoClose: 2000,
        },
      );
      onSuccess();
    } catch (e) {
      console.error(e);
      toast.error("Failed to publish tracking event. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldLabel = {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 500,
    mb: 0.5,
  };

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Type of Tracked Asset <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <Select
            disabled
            size="small"
            fullWidth
            name="assetType"
            value={formData.assetType}
            onChange={handleChange}
          >
            {[
              "Parcel",
              "Overpack",
              "Manifest",
              "Purchase Order",
              "Bill of Lading",
            ].map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Identifier <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <TextField
            disabled
            size="small"
            fullWidth
            name="identifier"
            value={formData.identifier}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Logistics Leg <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <AutocompleteWithPagination
            selectedClient={{ _id: clientId }}
            disabled
            selectedItem={{ _id: formData.logistics_leg }}
            onDataChange={(e) => {
              handleChange({
                target: { name: "logistics_leg", value: e?._id || "" },
              });
              logisticsLegRef.current = e?._id || "";
            }}
            hideLabel
            width="100%"
            optionKey="_id"
            apiEndPoint={(limit, skip, search, clientId) =>
              eventService.fetchLeg(search, clientId, limit, skip)
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Provider Event Code <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <AutocompleteWithPagination
            selectedClient={{ _id: clientId }}
            disabled={!formData.logistics_leg}
            selectedItem={{
              code: formData.eventCode,
              description: formData.eventDescription,
            }}
            onDataChange={(e) => updateEventCodeAndDescription(e)}
            hideLabel
            width="100%"
            optionKey={(item) => `${item.code} - ${item.description}`}
            apiEndPoint={(limit, skip, search, clientId) =>
              eventService.fetchCode(
                search,
                clientId,
                limit,
                skip,
                logisticsLegRef.current,
              )
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={fieldLabel}>Event Description</Typography>
          <TextField
            multiline
            disabled
            size="small"
            fullWidth
            name="eventDescription"
            value={formData.eventDescription}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Event Date & Time <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              value={
                formData.eventDateTime
                  ? dayjs(new Date(formData.eventDateTime))
                  : null
              }
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  eventDateTime: dayjs(val).format("YYYY-MM-DD HH:mm:ss"),
                }))
              }
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={fieldLabel}>
            Event Location <span style={{ color: "#DC585C" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            name="eventLocation"
            value={formData.eventLocation}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={fieldLabel}>Comment</Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            name="comment"
            value={formData.comment}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid() || submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Publishing..." : "Publish Event"}
        </Button>
      </DialogActions>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main Modal Wrapper
// ═══════════════════════════════════════════════════════════════════════════════
const KanbanStageModal = ({
  open,
  onClose,
  card,
  fromStage,
  toStage,
  onSuccess,
  addEventMode = false,
}) => {
  if (!card || !fromStage) return null;

  const title = addEventMode
    ? "Add Tracking Event"
    : TRANSITION_TITLES[fromStage] || "Stage Transition";

  const renderForm = () => {
    if (addEventMode) {
      return (
        <ManualTrackingModalForm
          card={card}
          fromStage="in_transit"
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    if (fromStage === "backlog") {
      return (
        <CreatePOModalForm
          card={card}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    if (fromStage === "po_issued") {
      return (
        <DispatchASNModalForm
          card={card}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    if (fromStage === "supplier_confirmed") {
      return (
        <ShipmentDateModalForm
          card={card}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    if (fromStage === "inventory_ready") {
      return (
        <VehicleInfoModalForm
          card={card}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    if (fromStage === "in_transit" || fromStage === "fulfillment") {
      return (
        <ManualTrackingModalForm
          card={card}
          fromStage={fromStage}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "var(--bg-paper, #1e1e2e)",
          backgroundImage: "none",
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          fontFamily: "Poppins",
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {title}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, pb: 0 }}>{renderForm()}</DialogContent>
    </Dialog>
  );
};

export default KanbanStageModal;
